import { CheckCircle2, KeyRound, Plug, Plus, ShieldAlert, Users, XCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { useRef, useState } from "react";
import { Button, cx, Empty, ErrorNote, Modal, Spinner, toast } from "../components/ui";
import { api, useApi } from "../lib/api";

type Product = "HCM" | "FSM";
interface TestResult {
  ok: boolean;
  token: { ok: boolean; ms: number; error?: string };
  lines: { product: Product; ok: boolean; status?: number; ms: number; error?: string }[];
  at: string;
}
interface Connection {
  connId: string;
  name: string;
  envLabel: string;
  productLines: Product[];
  servicePaths: Partial<Record<Product, string>>;
  production: boolean;
  concurrency: number;
  dataArea?: string;
  logicalId?: string;
  credential?: { fingerprint: string; ionTenant: string; gatewayHost: string; uploadedAt: string; uploadedBy: string };
  lastTest?: TestResult;
}

const when = (iso: string) => new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });

/** Tenant admin: Infor connections (design §5.1). The .ionapi file goes straight to the API and is never shown. */
export default function Connections() {
  const { data, error, loading, reload, setData } = useApi<{ connections: Connection[] }>("/admin/connections");
  const [editing, setEditing] = useState<Connection | "new" | null>(null);
  const replace = (c: Connection) => setData((d) => d && { connections: d.connections.map((x) => (x.connId === c.connId ? c : x)) });

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-ink">Infor connections</h1>
          <p className="text-sm text-muted">Each connection is one Infor CloudSuite tenant (PRD, TST…), reached with its ION API service account.</p>
        </div>
        <Button variant="primary" onClick={() => setEditing("new")}>
          <Plus className="size-4" aria-hidden /> Add connection
        </Button>
      </div>
      {error && <ErrorNote>{error}</ErrorNote>}
      {loading && !data ? (
        <Spinner />
      ) : data?.connections.length === 0 ? (
        <Empty icon={<Plug className="size-8" />} title="No connections yet">
          Add one for each Infor tenant your apps should work with.
        </Empty>
      ) : (
        <div className="space-y-4">
          {data?.connections.map((c) => (
            <ConnectionCard key={c.connId} c={c} onEdit={() => setEditing(c)} onChange={replace} reload={() => void reload()} />
          ))}
        </div>
      )}
      {editing && <ConnectionForm conn={editing === "new" ? undefined : editing} onClose={() => setEditing(null)} onSaved={() => (setEditing(null), void reload())} />}
    </div>
  );
}

function ConnectionCard({ c, onEdit, onChange, reload }: { c: Connection; onEdit: () => void; onChange: (c: Connection) => void; reload: () => void }) {
  const [testing, setTesting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const file = useRef<HTMLInputElement>(null);

  const upload = async (f: File | undefined) => {
    if (!f) return;
    setUploading(true);
    setError(null);
    try {
      const updated = await api<Connection>(`/admin/connections/${c.connId}/credential`, { method: "PUT", body: { ionapi: await f.text() } });
      onChange(updated);
      toast("Credentials saved. Run a test to check them.");
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setUploading(false);
      if (file.current) file.current.value = "";
    }
  };
  const test = async () => {
    setTesting(true);
    setError(null);
    try {
      await api(`/admin/connections/${c.connId}/test`, { method: "POST", body: {} });
      reload();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className={cx("rounded-xl border bg-surface p-5", c.production ? "border-bad" : "border-line")}>
      <div className="flex flex-wrap items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-display font-semibold text-ink">{c.name}</span>
            <span className={cx("text-xs rounded px-1.5 py-0.5 font-semibold", c.production ? "bg-bad text-white" : "bg-accent-chip text-accent-text")}>{c.envLabel}</span>
            {c.production && (
              <span className="text-xs text-bad inline-flex items-center gap-1">
                <ShieldAlert className="size-3.5" aria-hidden /> Production
              </span>
            )}
          </div>
          <div className="text-xs text-muted mt-1">
            {c.connId} · {c.productLines.map((p) => `${p} (${c.servicePaths[p]})`).join(", ")} · up to {c.concurrency} calls at once
          </div>
        </div>
        <div className="flex gap-2">
          <input ref={file} type="file" accept=".ionapi,application/json" className="hidden" onChange={(e) => void upload(e.target.files?.[0])} />
          <Button busy={uploading} onClick={() => file.current?.click()}>
            <KeyRound className="size-4" aria-hidden /> {c.credential ? "Replace .ionapi" : "Upload .ionapi"}
          </Button>
          <Button variant="primary" busy={testing} disabled={!c.credential} onClick={() => void test()}>
            Test
          </Button>
          {c.credential && (
            <Link to={`/admin/connections/${c.connId}/actors`} className="inline-flex items-center gap-1.5 rounded-lg border border-line px-3 h-9 text-sm font-medium text-ink-2 hover:bg-surface-2">
              <Users className="size-4" aria-hidden /> Actor mapping
            </Link>
          )}
          <Button variant="ghost" onClick={onEdit}>
            Edit
          </Button>
        </div>
      </div>
      <div className="mt-4 grid gap-3 sm:grid-cols-2 text-sm">
        <div className="rounded-lg bg-surface-2 p-3">
          <div className="text-xs font-medium text-muted mb-1">Credentials</div>
          {c.credential ? (
            <>
              <div className="text-ink">
                {c.credential.ionTenant} <span className="text-muted">via {c.credential.gatewayHost}</span>
              </div>
              <div className="text-xs text-muted">
                File {c.credential.fingerprint} · {when(c.credential.uploadedAt)} by {c.credential.uploadedBy}
              </div>
            </>
          ) : (
            <div className="text-muted">No file uploaded yet.</div>
          )}
        </div>
        <div className="rounded-lg bg-surface-2 p-3">
          <div className="text-xs font-medium text-muted mb-1">Last test</div>
          {!c.lastTest ? (
            <div className="text-muted">Not tested yet.</div>
          ) : (
            <ul className="space-y-0.5">
              <Check ok={c.lastTest.token.ok} label="Sign-in to ION API" detail={c.lastTest.token.error ?? `${c.lastTest.token.ms} ms`} />
              {c.lastTest.lines.map((l) => (
                <Check key={l.product} ok={l.ok} label={`${l.product} read`} detail={l.error ?? `${l.ms} ms`} />
              ))}
              <li className="text-xs text-muted pt-1">{when(c.lastTest.at)}</li>
            </ul>
          )}
        </div>
      </div>
      {error && (
        <div className="mt-3">
          <ErrorNote>{error}</ErrorNote>
        </div>
      )}
    </div>
  );
}

function Check({ ok, label, detail }: { ok: boolean; label: string; detail: string }) {
  const Icon = ok ? CheckCircle2 : XCircle;
  return (
    <li className="flex items-center gap-2">
      <Icon className={cx("size-4", ok ? "text-good" : "text-bad")} aria-hidden />
      <span className="text-ink">{label}</span>
      <span className="text-xs text-muted truncate">{detail}</span>
    </li>
  );
}

function ConnectionForm({ conn, onClose, onSaved }: { conn?: Connection; onClose: () => void; onSaved: () => void }) {
  const [f, setF] = useState({
    connId: conn?.connId ?? "",
    name: conn?.name ?? "",
    envLabel: conn?.envLabel ?? "TST",
    productLines: conn?.productLines ?? (["HCM"] as Product[]),
    servicePaths: { HCM: "LAWSONGHR", FSM: "FSM", ...conn?.servicePaths },
    production: conn?.production ?? false,
    concurrency: conn?.concurrency ?? 4,
    dataArea: conn?.dataArea ?? "",
    logicalId: conn?.logicalId ?? "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const body = { ...f, servicePaths: Object.fromEntries(f.productLines.map((p) => [p, f.servicePaths[p]])) };
      if (conn) await api(`/admin/connections/${conn.connId}`, { method: "PUT", body });
      else await api("/admin/connections", { body });
      toast(conn ? "Connection saved" : "Connection added. Upload its .ionapi file next.");
      onSaved();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const input = "w-full h-9 rounded-lg border border-line bg-surface px-3 text-sm";
  return (
    <Modal
      title={conn ? `Edit ${conn.name}` : "Add connection"}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" busy={busy} onClick={() => void save()}>
            {conn ? "Save" : "Add connection"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && <ErrorNote>{error}</ErrorNote>}
        <div className="grid grid-cols-3 gap-3">
          <label className="block text-sm col-span-2">
            <span className="text-ink-2 font-medium">Name</span>
            <input className={input} value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="Fresno TST" />
          </label>
          <label className="block text-sm">
            <span className="text-ink-2 font-medium">Environment</span>
            <input className={input} value={f.envLabel} onChange={(e) => set("envLabel", e.target.value.toUpperCase())} placeholder="TST" />
          </label>
        </div>
        {!conn && (
          <label className="block text-sm">
            <span className="text-ink-2 font-medium">Connection id</span>
            <input className={input} value={f.connId} onChange={(e) => set("connId", e.target.value.toLowerCase())} placeholder="tst" />
            <span className="text-xs text-muted">Short and permanent, such as prd or tst.</span>
          </label>
        )}
        <fieldset className="text-sm space-y-2">
          <legend className="text-ink-2 font-medium mb-1">Product lines and ION API service paths</legend>
          {(["HCM", "FSM"] as Product[]).map((p) => (
            <div key={p} className="flex items-center gap-3">
              <label className="flex items-center gap-2 w-20">
                <input type="checkbox" checked={f.productLines.includes(p)} onChange={(e) => set("productLines", e.target.checked ? [...f.productLines, p] : f.productLines.filter((x) => x !== p))} />
                {p}
              </label>
              <input className={cx(input, "flex-1")} disabled={!f.productLines.includes(p)} value={f.servicePaths[p]} onChange={(e) => set("servicePaths", { ...f.servicePaths, [p]: e.target.value })} />
            </div>
          ))}
          <p className="text-xs text-muted">From Infor OS, ION API, Available APIs. HCM is usually LAWSONGHR and FSM is usually FSM.</p>
        </fieldset>
        <label className="block text-sm">
          <span className="text-ink-2 font-medium">Concurrent Infor calls</span>
          <input className={cx(input, "w-24")} type="number" min={1} max={16} value={f.concurrency} onChange={(e) => set("concurrency", Number(e.target.value))} />
          <span className="block text-xs text-muted">Calls beyond this wait their turn. Default 4, maximum 16.</span>
        </label>
        <label className="block text-sm">
          <span className="text-ink-2 font-medium">Data area</span>
          <input className={cx(input, "w-48")} value={f.dataArea} onChange={(e) => set("dataArea", e.target.value.trim())} placeholder="e.g. prd" />
          <span className="block text-xs text-muted">The Landmark data area named in background requests. Job Automation needs it to run background steps.</span>
        </label>
        <label className="block text-sm">
          <span className="text-ink-2 font-medium">Logical id</span>
          <input className={input} value={f.logicalId} onChange={(e) => set("logicalId", e.target.value.trim())} placeholder="lid://infor.lawson-ghr.ghr110-prod-useast1" />
          <span className="block text-xs text-muted">Optional. Sent to Infor as x-infor-logicalidprefix on every call. RADAR needs it for GenAI and Compass.</span>
        </label>
        <label className="flex items-start gap-2 text-sm">
          <input type="checkbox" className="mt-1" checked={f.production} onChange={(e) => set("production", e.target.checked)} />
          <span>
            <span className="font-medium text-ink">Production</span>
            <span className="block text-xs text-muted">Writes need a role allowed to change production, and runs ask for confirmation.</span>
          </span>
        </label>
      </div>
    </Modal>
  );
}
